import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { FieldValue } from 'firebase-modules';
import { BaseCard, FILENAME_SUFFIX_FIELD, FirestoreCard, CardEvent } from '@creative-kit/shared';
import { matchesAnyNormalizedSearchTerm, normalizeSearchText } from 'utils/textSearch';
import type { RootStore } from '.';
import { db } from './data';
import type { Session } from './session';
import { emitCardEvent } from './card-events';
import { getCardEventsStateAfterListenerError } from './cardEventUtils';

type ModificationState = 'deleting' | 'updating' | false;

export class Card extends BaseCard {
  protected rootStore: RootStore;
  private ref;
  modificationState: ModificationState = false;
  events: CardEvent[] = [];
  private eventsLoaded = false;
  private eventsUnsubscribe?: () => void;

  constructor(
    id: string,
    sessionId: string,
    data: FirestoreCard,
    rootStore: RootStore,
    previewIndex?: number,
  ) {
    super(id, sessionId, data, rootStore, previewIndex);

    makeObservable(this, {
      data: observable.struct,
      modificationState: observable,
      events: observable,
      updateData: action,
      cardIdx: computed,
      cardNumber: computed,
      isEditable: computed,
      isVisible: computed,
      isSelected: computed,
      isDeleting: computed,
      isUpdating: computed,
      filenameInfo: computed,
      filenameNeedsAttention: computed,
      status: computed,
      suffix: computed,
      permalink: computed,
      isFocused: computed,
      unloadEvents: action.bound,
      setSuffix: action,
      setModificationState: action,
      resetMotificationState: action,
    });

    this.rootStore = rootStore;
    this.ref = db.sessionCards(this.session.id).doc(this.id);
  }

  updateData(newData: FirestoreCard) {
    this.data = newData;
  }

  /**
   * Subscribe to the card's events subcollection for the History section.
   * Only loads once — subsequent calls are no-ops until unsubscribed.
   */
  loadEvents() {
    if (this.eventsLoaded) return;
    this.eventsLoaded = true;

    const eventsRef = db.cardEvents(this.sessionId, this.id);
    this.eventsUnsubscribe = eventsRef.orderBy('timestamp', 'desc').onSnapshot(
      (snapshot) => {
        const loaded: CardEvent[] = snapshot.docs.map((doc) => doc.data());
        runInAction(() => {
          this.events = loaded;
        });
      },
      (error) => {
        const resetState = getCardEventsStateAfterListenerError();
        runInAction(() => {
          this.events = resetState.events;
          this.eventsLoaded = resetState.eventsLoaded;
          this.eventsUnsubscribe = undefined;
        });
        // eslint-disable-next-line no-console
        console.error('[Card] Events listener error:', error.code, error.message);
      },
    );
  }

  unloadEvents() {
    if (this.eventsUnsubscribe) {
      this.eventsUnsubscribe();
      this.eventsUnsubscribe = undefined;
    }
    this.eventsLoaded = false;
    this.events = [];
  }

  toggleSelection() {
    const {
      uiActiveSessionStore: { toggleSelectedCard },
    } = this.rootStore;
    return toggleSelectedCard(this.id);
  }

  get isDeleting() {
    return this.modificationState === 'deleting';
  }

  // Override the base cardNumber to use race-condition-safe calculation
  get cardNumber(): number {
    return (this.session as Session).getCardDisplayNumber?.(this.id) ?? super.cardNumber;
  }

  get isUpdating() {
    return this.modificationState === 'updating';
  }

  get isEditable() {
    // Deleted cards should not be editable
    if (this.data.deletedAt) {
      return false;
    }
    return super.isEditable;
  }

  setModificationState(state: ModificationState) {
    this.modificationState = state;
  }

  resetMotificationState() {
    this.setModificationState(false);
  }

  get isSelected() {
    const {
      uiActiveSessionStore: { isCardSelected },
    } = this.rootStore;
    return isCardSelected(this.id);
  }

  /*
  Check whether a card is visible based on the card search
  If a search term is entered, only show cards where the search
  term appears in one of the displayed fields of one of the modules
  
  Performance optimized with early short-circuits and cached checks
  */
  get isVisible() {
    return this.isVisibleWithOverrides();
  }

  isVisibleWithOverrides(options?: { ignorePriorityFilters?: boolean }) {
    const {
      sessionsStore,
      uiPermissions,
      uiCardSearchStore,
      uiModulePowerColumnStore,
      uiActiveSessionStore,
    } = this.rootStore;
    const { currentSession } = sessionsStore;

    // Early exit checks - fastest first
    if (!currentSession || !uiPermissions.canViewCurrentSession) {
      return false;
    }

    // Check issue filters
    if (uiActiveSessionStore.isIssueFilterActive) {
      if (this.data.issue?.status !== 'open') {
        return false; // Issue filter active but card has no open issue
      }
    }
    if (uiActiveSessionStore.noIssuesFilterActive) {
      if (this.data.issue?.status === 'open') {
        return false; // No-issues filter active but card has open issue
      }
    }

    // Check date filters
    if (uiActiveSessionStore.hasDatesFilterActive) {
      if (!this.data.dates || this.data.dates.length === 0) {
        return false; // Has dates filter active but card has no dates
      }
    }
    if (uiActiveSessionStore.noDatesFilterActive) {
      if (this.data.dates && this.data.dates.length > 0) {
        return false; // No-dates filter active but card has dates
      }
    }

    // Check priority filters (priority selection + no-priority work together as OR)
    if (!options?.ignorePriorityFilters) {
      const { priorityFilter, noPriorityFilterActive } = uiActiveSessionStore;
      const hasPriorityFilters = priorityFilter.size > 0 || noPriorityFilterActive;
      if (hasPriorityFilters) {
        const cardPriority = this.data.priority;
        const matchesPrioritySelection =
          priorityFilter.size > 0 && cardPriority && priorityFilter.has(cardPriority);
        const matchesNoPriority = noPriorityFilterActive && !cardPriority;

        if (!matchesPrioritySelection && !matchesNoPriority) {
          return false; // Priority filter active but card doesn't match any selected priority or no-priority
        }
      }
    }

    // Check row selections first - this filter must always apply regardless of search state
    const { hasRowSelection, isCardVisibleBasedOnRowSelections } = uiModulePowerColumnStore;
    if (hasRowSelection && !isCardVisibleBasedOnRowSelections(this)) {
      return false; // Failed row filter = not visible
    }

    const { isFilteringInProgress } = currentSession;
    if (!isFilteringInProgress && !uiActiveSessionStore.hasAnyQuickFilters) {
      return true; // No filtering = all visible (after row selection, issue, date, and priority checks)
    }

    // Quick card number search check (very fast)
    const { currentSearch } = uiCardSearchStore;
    if (currentSearch) {
      const numberSearchMatches = currentSearch.match(/^#([0-9]+)$/);
      if (numberSearchMatches) {
        return this.cardNumber.toString() === numberSearchMatches[1];
      }
    }

    // Text search checks (more expensive)
    const { searchTerms, hasSearch } = uiCardSearchStore;
    if (!hasSearch || searchTerms.length === 0) {
      return true; // Passed row filter, no search terms
    }

    // Check module connections (usually fastest search check)
    const { searchMatchingRowsByModuleId } = uiCardSearchStore;
    for (const [moduleId, searchMatchingRows] of Object.entries(searchMatchingRowsByModuleId)) {
      const connectedRows = this.getConnectedRows(moduleId);
      if (connectedRows.some((rId) => searchMatchingRows.has(rId))) {
        return true; // Found match in module
      }
    }

    // Check title, filename, messages (lazy evaluation)
    return (
      this.doesSearchMatchTitle || this.doesSearchMatchFilename || this.doesSearchMatchMessages
    );
  }

  get doesSearchMatchTitle() {
    const {
      uiCardSearchStore: { searchTerms },
      sessionsStore: { currentSession },
      uiPermissions: { canViewCurrentSession },
    } = this.rootStore;

    if (!currentSession || !canViewCurrentSession) {
      return false;
    }

    const { isFilteringInProgress, includeTitleInQueueSearch } = currentSession;

    if (!isFilteringInProgress || !includeTitleInQueueSearch) {
      return false;
    }

    return matchesAnyNormalizedSearchTerm(this.mainLabel, searchTerms);
  }

  get doesSearchMatchFilename() {
    const {
      uiCardSearchStore: { searchTerms },
      sessionsStore: { currentSession },
      uiPermissions: { canViewCurrentSession },
    } = this.rootStore;

    if (!currentSession || !canViewCurrentSession) {
      return false;
    }

    const { isFilteringInProgress, includeFilenameInQueueSearch } = currentSession;

    if (!isFilteringInProgress || !includeFilenameInQueueSearch) {
      return false;
    }

    return searchTerms.some((term) => {
      const normalizedFilename = normalizeSearchText(this.filename);
      const doesSearchMatchFilename = normalizedFilename.includes(term);
      const doesSearchPartialMatchFilename =
        normalizedFilename.length > 0 ? term.includes(normalizedFilename) : false;
      return doesSearchMatchFilename || doesSearchPartialMatchFilename;
    });
  }

  get doesSearchMatchMessages() {
    const {
      uiCardSearchStore: { searchTerms },
      sessionsStore: { currentSession },
      uiPermissions: { canViewCurrentSession },
      commentsStore: { commentsByCard },
    } = this.rootStore;

    if (!currentSession || !canViewCurrentSession) {
      return false;
    }

    const { isFilteringInProgress, includeMessagesInQueueSearch } = currentSession;

    if (!isFilteringInProgress || !includeMessagesInQueueSearch) {
      return false;
    }

    const comments = commentsByCard[this.id];
    if (!comments) {
      return false;
    }

    return comments.some((comment) =>
      matchesAnyNormalizedSearchTerm(comment.data.text, searchTerms),
    );
  }

  /**
   * Check if a card's filename needs attention, that is, if the card is marked
   * done, and its filename would change based on new formula or settings
   */
  get filenameNeedsAttention() {
    const { filename, filenameUpdateIgnoredAt } = this.data;
    const { filenameParametersUpdatedAt } = this.session.data;

    if (!(filename && filenameParametersUpdatedAt)) {
      return false;
    }

    const { errors } = this.filenameInfo;
    const computedFilename = this.filename;
    return (
      errors.length === 0 &&
      computedFilename !== filename &&
      (!filenameUpdateIgnoredAt || filenameUpdateIgnoredAt < filenameParametersUpdatedAt)
    );
  }

  get visibleModules() {
    const session = this.session as Session;
    const sessionVisibleModules = [...session.getVisibleModulesForStep(this.data.stepId)];

    if (session.isFilteringInProgress) {
      // Handle search-based temporary module visibility (same pattern as row filtering)
      Object.entries(this.rootStore.uiCardSearchStore.searchMatchingRowsByModuleId).forEach(
        ([moduleId, searchMatchingRows]) => {
          if (this.getConnectedRows(moduleId).some((rId) => searchMatchingRows.has(rId))) {
            if (!sessionVisibleModules.find((m) => m.id === moduleId)) {
              sessionVisibleModules.push(session.data.modules[moduleId]);
            }
          }
        },
      );

      // Handle row selection-based temporary module visibility
      Object.entries(this.rootStore.uiModulePowerColumnStore.rowSelectionsByModuleId).forEach(
        ([moduleId, selectedRowsForModule]) => {
          if (this.getConnectedRows(moduleId).some((rId) => selectedRowsForModule.has(rId))) {
            if (!sessionVisibleModules.find((m) => m.id === moduleId)) {
              sessionVisibleModules.push(session.data.modules[moduleId]);
            }
          }
        },
      );
    }

    return sessionVisibleModules;
  }

  // Back-end operations
  acceptNewFilename = () => {
    if (this.filenameNeedsAttention) {
      this.ref.set(
        {
          filename: this.filename,
        },
        { merge: true },
      );
    }
  };

  ignoreNewFilename = () => {
    if (this.filenameNeedsAttention) {
      this.ref.set(
        {
          filenameUpdateIgnoredAt: FieldValue.serverTimestamp() as unknown as Date,
        },
        { merge: true },
      );
    }
  };

  get suffix(): string {
    if (this.data.suffix) {
      return this.data.suffix;
    }
    if (this.rootStore.sessionsStore.currentSession?.automaticFilenameVersioning) {
      const sessionCards = (this.session.cardList as Card[]) ?? [];
      const duplicateFilenameCards = sessionCards
        .filter((c) => this.unsuffixedFilename === c.unsuffixedFilename)
        .sort((a, b) => a.data.createdAt.getTime() - b.data.createdAt.getTime());
      const currentIdx = duplicateFilenameCards.findIndex((c) => c.id === this.id);
      return currentIdx > 0 ? `V${currentIdx + 1}` : '';
    }
    return '';
  }

  setSuffix(newSuffix: string) {
    /**
     * When suffix is updated, add filename suffix to card title formula if not already present
     */
    if (
      this.rootStore.sessionsStore?.currentSession?.automaticCardTitleSuffix &&
      this.rootStore.uiCardTitleStore.getFieldIndex(FILENAME_SUFFIX_FIELD) === -1
    ) {
      this.rootStore.uiCardTitleStore.addField(FILENAME_SUFFIX_FIELD);
      this.rootStore.uiCardTitleStore.saveFormula();
    }
    this.ref.set(
      {
        suffix: newSuffix,
      },
      { merge: true },
    );
  }

  setManualTitle(newTitle: string) {
    this.ref.set(
      {
        manualTitle: newTitle,
      },
      { merge: true },
    );
    const user = this.rootStore.authStore.user;
    emitCardEvent(this.sessionId, this.id, 'title.changed', user?.uid ?? '', user?.email ?? null, {
      title: newTitle,
    });
  }

  clearManualTitle() {
    this.ref.update({
      manualTitle: FieldValue.delete(),
    });
    const user = this.rootStore.authStore.user;
    emitCardEvent(this.sessionId, this.id, 'title.changed', user?.uid ?? '', user?.email ?? null, {
      cleared: true,
    });
  }

  createCommentIfNeeded() {
    const { uiCommentsStore } = this.rootStore;
    const composedComment = uiCommentsStore.composedCommentsByCardId[this.id] || '';
    const cleanComment = composedComment.trim();
    if (cleanComment) {
      (this.session as Session).createComment(this.id, cleanComment);
    }
    uiCommentsStore.setComposedComment(this.id, '');
  }

  removeRowOnly = async (moduleId: string, rowId: string) => {
    const moduleConnections = this.data.connections[moduleId];
    const user = this.rootStore.authStore.user;

    if (typeof moduleConnections === 'string' && moduleConnections === rowId) {
      await this.ref.update({
        [`connections.${moduleId}`]: null,
      });

      emitCardEvent(
        this.sessionId,
        this.id,
        'connection.removed',
        user?.uid ?? '',
        user?.email ?? null,
        {
          moduleId,
          rowIds: [rowId],
        },
      );

      return () =>
        this.ref.update({
          [`connections.${moduleId}`]: moduleConnections,
        });
    }
    if (Array.isArray(moduleConnections)) {
      await this.ref.update({
        [`connections.${moduleId}`]: FieldValue.arrayRemove(rowId),
      });

      emitCardEvent(
        this.sessionId,
        this.id,
        'connection.removed',
        user?.uid ?? '',
        user?.email ?? null,
        {
          moduleId,
          rowIds: [rowId],
        },
      );

      return () =>
        this.ref.update({
          [`connections.${moduleId}`]: FieldValue.arrayUnion(rowId),
        });
    }
  };

  removeRow = async (moduleId: string, rowId: string) => {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return;
    const {
      data: { modules },
    } = currentSession;
    const module = modules[moduleId];
    if (!module) {
      return;
    }
    const undo = await this.removeRowOnly(moduleId, rowId);

    if (undo !== undefined) {
      this.rootStore.uiSnackbarStore.setUndoDetails(
        undo,
        `Item from ${module.name} removed from card #${this.cardNumber}`,
      );
    }
  };

  get permalink() {
    // Generate URL with /project/... pattern (new user-facing format)
    return `http://${window.location.host}/project/${this.rootStore.sessionsStore.currentSessionId}/card/${this.id}`;
  }

  get isFocused() {
    return this.rootStore.uiCardView.selectedCardId === this.id;
  }

  focusCard() {
    this.rootStore.uiCardView.toggleCardFocus(this.id);
  }

  removeConnection = async (moduleId: string, rowId: string) => {
    const undo = await this.removeRowOnly(moduleId, rowId);
    if (undo) {
      const module = this.session.data.modules[moduleId];
      this.rootStore.uiSnackbarStore.setUndoDetails(
        undo,
        `Item from ${module.name} removed from card #${this.cardNumber}`,
      );
    }
  };
}
